import React, { useState } from 'react';
import MetricsHeader from './MetricsHeader';
import MetricsTablesSection from './MetricsTablesSection';
import EditMetricModal from './EditMetricModal';
import ExtractMetricsModal from './modals/ExtractMetricsModal';
import { DeleteMetricModal } from './MetricsModals';
import type { CompanyPricingMetricsProps, Metric } from './types/metricTypes';
import { useMetricsDataV2 } from './hooks/useMetricsDataV2';
import { useMetricsMutations } from './hooks/useMetricsMutations';

const CompanyPricingMetrics: React.FC<CompanyPricingMetricsProps> = ({
  pageSubslug = 'main',
  refreshCompanyData
}) => {
  // Data hook using shared React Query data source
  const {
    company,
    categorizedMetrics,
    showCurrentOnly,
    setShowCurrentOnly,
    actualVersion,
    isLoading
  } = useMetricsDataV2(pageSubslug);

  // Modal and form state
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [metricToDelete, setMetricToDelete] = useState<Metric | null>(null);
  const [showEditMetricModal, setShowEditMetricModal] = useState(false);
  const [metricToEdit, setMetricToEdit] = useState<Metric | null>(null);
  const [showAddMetricModal, setShowAddMetricModal] = useState(false);
  const [newMetricName, setNewMetricName] = useState('');
  const [newMetricModality, setNewMetricModality] = useState<'Flat' | 'License' | 'Credit' | 'Usage' | 'Threshold' | 'Unknown'>('Usage');
  const [isAddingMetric, setIsAddingMetric] = useState(false);
  const [showExtractModal, setShowExtractModal] = useState(false);

  // Mutation handlers
  const { handleAddMetric, handleMetricUpdated, handleMetricDeleted } = useMetricsMutations({
    pageSubslug,
    setNewMetricName,
    setShowAddMetricModal,
    setIsAddingMetric,
    setMetricToEdit,
    setShowEditMetricModal,
    setMetricToDelete,
    setDeleteDialogOpen,
    newMetricName,
    newMetricModality,
    refreshCompanyData
  });

  const handleToggleChange = (checked: boolean) => {
    setShowCurrentOnly(checked);
  };

  const handleRefreshMetrics = () => {
    setShowExtractModal(true);
  };

  const handleDeleteMetric = (metric: Metric) => {
    setMetricToDelete(metric);
    setDeleteDialogOpen(true);
  };

  const handleEditMetric = (metric: Metric) => {
    setMetricToEdit(metric);
    setShowEditMetricModal(true);
  };

  return (
    <div>
      <MetricsHeader
        showCurrentOnly={showCurrentOnly}
        actualVersion={actualVersion}
        categorizedMetrics={categorizedMetrics}
        onToggleCurrentOnly={handleToggleChange}
        onRunExtracts={handleRefreshMetrics}
        onAddMetric={() => setShowAddMetricModal(true)}
      />
      {categorizedMetrics.usageLicenseCreditMetrics.length + categorizedMetrics.thresholdFlatMetrics.length + categorizedMetrics.otherMetrics.length > 0 ? (
        <MetricsTablesSection
          categorizedMetrics={categorizedMetrics}
          onDeleteMetric={handleDeleteMetric}
          onEditMetric={handleEditMetric}
        />
      ) : (
        <div className="flex flex-col items-center justify-center py-8 text-center">
          <p className="text-gray-500 mb-1">No pricing metrics available</p>
          {company?.slug && (
            <button
              className="mt-4 px-4 py-2 border rounded"
              onClick={handleRefreshMetrics}
            >
              Refresh Metrics
            </button>
          )}
        </div>
      )}

      <DeleteMetricModal
        isOpen={deleteDialogOpen}
        setIsOpen={setDeleteDialogOpen}
        metric={metricToDelete}
        onDeleted={() => {
          if (metricToDelete?.id) {
            handleMetricDeleted(metricToDelete.id);
          }
        }}
      />

      <EditMetricModal
        metric={metricToEdit}
        isOpen={showEditMetricModal}
        onClose={() => {
          setShowEditMetricModal(false);
          setMetricToEdit(null);
        }}
        onSuccess={handleMetricUpdated}
      />

      {showAddMetricModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6 relative">
            <button
              onClick={() => setShowAddMetricModal(false)}
              className="absolute top-4 right-4 text-gray-400 hover:text-gray-600"
            >
              ×
            </button>
            <h2 className="text-xl font-semibold mb-4">Add New Metric</h2>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Metric Name</label>
                <input
                  type="text"
                  value={newMetricName}
                  onChange={(e) => setNewMetricName(e.target.value)}
                  placeholder="Enter metric name"
                  className="w-full p-2 border rounded-md"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Modality</label>
                <select
                  value={newMetricModality}
                  onChange={(e) => setNewMetricModality(e.target.value as any)}
                  className="w-full p-2 border rounded-md bg-white"
                >
                  <option value="Usage">Usage</option>
                  <option value="License">License</option>
                  <option value="Credit">Credit</option>
                  <option value="Flat">Flat</option>
                  <option value="Threshold">Threshold</option>
                  <option value="Unknown">Unknown</option>
                </select>
              </div>
              <div className="pt-4 border-t flex justify-end space-x-3">
                <button
                  onClick={() => setShowAddMetricModal(false)}
                  className="px-4 py-2 border rounded-md text-gray-700 hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  onClick={handleAddMetric}
                  disabled={!newMetricName.trim() || isAddingMetric}
                  className={`px-4 py-2 rounded-md bg-blue-600 text-white ${!newMetricName.trim() || isAddingMetric ? 'opacity-50 cursor-not-allowed' : 'hover:bg-blue-700'}`}
                >
                  {isAddingMetric ? 'Adding...' : 'Add Metric'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      <ExtractMetricsModal
        isOpen={showExtractModal}
        onOpenChange={setShowExtractModal}
        slug={company?.slug || ''}
        subslug={pageSubslug}
      />
    </div>
  );
};

export default CompanyPricingMetrics;
